/**
 * Insights promotions & narratives — types and calls.
 *
 * Mirrors the staff surface under /api/v1/insights/* (packs, the
 * marketplace, runs, promotions, narratives) and the club-facing
 * /api/v1/insights/promoted feed.
 */
import { api } from '../lib/api'
import { pathSeg } from '../lib/urls'
import type { Theme } from '../theme/theme'
import { pageWithViewAs, withViewAs } from '../lib/viewAs'

export type PromotionStatus = 'draft' | 'published' | 'retired'

export type LabPack = {
  id: number
  name: string
  version: string
  title: string | null
  description: string | null
  /** 'marketplace' when imported from the insight-packs marketplace. */
  source: 'builtin' | 'bundle' | 'marketplace'
  /** 'insight-pack' (or 'serve-page', the first packs' name for it);
   *  'legacy' on a row the earlier mechanism registered. */
  kind: string
  /** True on a row nothing can build any more: remove is the only action. */
  legacy: boolean
  requires: { tables?: string[] }
  /** The gamedata keys the pack's queries produce. */
  data_shape: string[]
  registered_by_id: number | null
  created_at: string | null
}

export type LabRun = {
  id: number
  org_id: number
  org_code: string
  org_name: string
  game_code: string
  game_id: number
  pack_id: string
  pack_version: string
  computed_at: string | null
  payload_count: number | null
  status: 'queued' | 'running' | 'succeeded' | 'failed'
  started_at: string | null
  finished_at: string | null
  error: string | null
  has_artifact: boolean
  published_at: string | null
}

// ── report-coverage matrix (the Builds tab) ─────────────────────────────────

export type CoveragePack = {
  name: string; version: string; title: string
  /** The manifest's description; null when the pack has none. */
  description?: string | null
}

export type CoverageGame = {
  game_id: number
  game_code: string
  org_id: number
  org_code: string
  org_name: string
  org_logo_url: string | null
  match_date: string | null
  /** Switched on for layer-stack serving — the precondition for any build. */
  served: boolean
}

export type CoverageCell = {
  status: 'queued' | 'running' | 'succeeded' | 'failed'
  published: boolean
  has_artifact: boolean
}

export type Coverage = {
  packs: CoveragePack[]
  games: CoverageGame[]
  /** Keyed "<game_id>:<name>@<version>"; sparse — only where a run exists. */
  cells: Record<string, CoverageCell>
}

export const getCoverage = (club?: number) =>
  api<Coverage>(`/api/v1/insights/coverage${club ? `?club=${club}` : ''}`)

/** One pack's outcome from a club-scoped build request. */
export type ClubBuildPack = {
  name: string
  version: string
  served_games: number
  queued: number
  already_in_flight: number
  reaped: number
  /** only=failed: served games skipped because their last build did not fail */
  not_failed?: number
  /** How the builder started ('in-flight' when one was already working). */
  execution?: string
  /** Present when this pack's builder could not start (its queued rows
   *  were failed); the other packs still run. */
  error?: string
}

export type ClubBuildResult = {
  org_id: number
  org_code: string
  packs: ClubBuildPack[]
}

/** Run one pack — or every registered pack — on one club's served games. */
export const buildClubPacks = (
  orgId: number,
  packs: 'all' | { name: string; version: string }[],
  force = false,
  onlyFailed = false,
) =>
  api<ClubBuildResult>(`/api/v1/insights/clubs/${orgId}/build`, {
    method: 'POST',
    body: JSON.stringify(onlyFailed ? { packs, force, only: 'failed' } : { packs, force }),
  })

/** The order a match's reports read in on the club's Insights Feed:
 *  ordered packs first, then the rest by title. `source`: whether staff
 *  saved it or it is the default. */
export type PackOrder = {
  source: 'saved' | 'default'
  packs: { name: string; title: string; ordered: boolean }[]
  updated_at: string | null
  updated_by: string | null
}

export const getPackOrder = () => api<PackOrder>('/api/v1/insights/pack-order')

/** Save the whole order (names, most general first); [] returns to the
 *  default. Platform-admin. */
export const setPackOrder = (order: string[], reason: string) =>
  api<PackOrder>('/api/v1/insights/pack-order', {
    method: 'PUT', body: JSON.stringify({ order, reason }),
  })

export type ClubPublishResult = {
  org_id: number
  org_code: string
  game_code: string | null
  published: number
  already: number
  stale: number
  not_green: Record<string, number>
  pages: { game_code: string; name: string; version: string }[]
}

/** Publish to the club every GREEN report of these packs — on all its
 *  games, or one (`gameCode`). A page built before the game's serve tables
 *  changed is skipped and counted `stale`, as the single-game publish
 *  refuses it. */
export const publishClubGreen = (
  orgId: number,
  packs: { name: string; version: string }[],
  gameCode: string | null,
  reason: string,
) =>
  api<ClubPublishResult>(`/api/v1/insights/clubs/${orgId}/publish`, {
    method: 'POST',
    body: JSON.stringify({ packs, reason, ...(gameCode ? { game_code: gameCode } : {}) }),
  })

export type ClubUnpublishResult = {
  org_id: number
  org_code: string
  game_code: string | null
  unpublished: number
  pages: { game_code: string; name: string; version: string }[]
}

/** Take these packs' reports back from the club — every published version
 *  of each — on all its games, or one (`gameCode`). The pages stay built
 *  and can be published again. */
export const unpublishClub = (
  orgId: number,
  packs: { name: string }[],
  gameCode: string | null,
  reason: string,
) =>
  api<ClubUnpublishResult>(`/api/v1/insights/clubs/${orgId}/unpublish`, {
    method: 'POST',
    body: JSON.stringify({ packs: packs.map(({ name }) => ({ name })), reason,
                           ...(gameCode ? { game_code: gameCode } : {}) }),
  })

export type Promotion = {
  id: number
  org_id: number
  org_code: string
  game_code: string
  pack_id: string
  pack_version: string
  source_ref: string | null
  headline: string
  caption: string | null
  status: PromotionStatus
  published_at: string | null
  has_html: boolean
  html_width: number | null
  html_height: number | null
  created_at: string
}

export type NarrativeItem = {
  id: number
  promotion_id: number
  position: number
  caption_override: string | null
  headline: string
  image_url: string
}

export type Narrative = {
  id: number
  org_id: number
  org_code: string
  title: string
  scope_label: string | null
  intro: string | null
  status: PromotionStatus
  published_at: string | null
  items: NarrativeItem[]
}

export type PromotedHtml = { url: string; width: number; height: number }

export type PromotedItem = {
  id: number
  headline: string
  caption: string | null
  game_code: string
  image_url: string
  html: PromotedHtml | null
}

export type PromotedNarrative = {
  id: number
  title: string
  scope_label: string | null
  intro: string | null
  items: (PromotedItem & { promotion_id: number })[]
}

export type PromotedFeed = {
  promotions: PromotedItem[]
  narratives: PromotedNarrative[]
}

export const listLabPacks = () =>
  api<{ packs: LabPack[] }>('/api/v1/insights/packs')

/** One published version, graded against this environment's registry.
 *  available · installed (same name@version, same sha256) · conflict
 *  (same name@version registered with other bytes; never overwritten) ·
 *  broken (its index entry does not validate; `error` says why). */
export type VersionStatus = 'available' | 'installed' | 'conflict' | 'broken'

export type MarketplaceVersion = {
  version: string
  status: VersionStatus
  title?: string | null
  description?: string | null
  kind?: string | null
  sha256?: string
  bytes?: number
  queries?: number | null
  source_commit?: string | null
  /** When this version was published (UTC ISO): the index's own date, else
   *  its bundle's upload time in the bucket. */
  published_at?: string | null
  error?: string
}

/** A pack's state: installed (latest is installed) · update (an older
 *  version is installed) · older (what is installed is NEWER than the
 *  catalog's latest — installing it would be a downgrade) · conflict
 *  (latest conflicts) · new · broken (no usable version; `error` says why). */
export type PackState = 'installed' | 'update' | 'older' | 'conflict' | 'new' | 'broken'

export type MarketplacePack = {
  name: string
  latest: string | null
  state: PackState
  /** Why a broken pack cannot be installed. */
  error?: string | null
  title: string | null
  description: string | null
  installed_versions: string[]
  /** Newest first. */
  versions: MarketplaceVersion[]
}

export type Marketplace = {
  configured: boolean
  bucket?: string
  packs: MarketplacePack[]
  /** True when the bucket has no packs/index.json yet. */
  index_missing?: boolean
  generated_at?: string | null
  generated_by?: string | null
}

/** The catalog (packs/index.json) the insight-packs repo publishes to this
 *  environment. refresh=true revalidates it instead of the server's 120 s
 *  cached read. */
export const listMarketplace = (refresh = false) =>
  api<Marketplace>(`/api/v1/insights/marketplace${refresh ? '?refresh=1' : ''}`)

/** Install one version. imported=false: these bytes were already installed. */
export const importRelease = (name: string, version: string) =>
  api<LabPack & { imported: boolean }>(
    `/api/v1/insights/marketplace/${pathSeg(name)}/${pathSeg(version)}/import`,
    { method: 'POST' },
  )

/** Remove one registered version. force=true removes its builds too. */
export const deletePack = (name: string, version: string, force = false) =>
  api<{ deleted: string }>(
    `/api/v1/insights/packs/${pathSeg(name)}/${pathSeg(version)}${force ? '?force=true' : ''}`,
    { method: 'DELETE' },
  )

export const listRuns = () =>
  api<{ runs: LabRun[] }>('/api/v1/insights/runs')

export const listPromotions = () =>
  api<{ promotions: Promotion[] }>('/api/v1/insights/promotions')

export const patchPromotion = (
  id: number,
  body: { headline?: string; caption?: string; status?: PromotionStatus },
) => api<Promotion>(`/api/v1/insights/promotions/${id}`, {
  method: 'PATCH', body: JSON.stringify(body),
})

export const deletePromotion = (id: number) =>
  api(`/api/v1/insights/promotions/${id}`, { method: 'DELETE' })

// Staff-only Lab addresses, and the image one is saved into narratives:
// never with a view-as grant in them.
export const promotionImageUrl = (id: number) =>
  `/api/v1/insights/promotions/${id}/image`

export const promotionHtmlUrl = (id: number) =>
  `/api/v1/insights/promotions/${id}/html`

export const listNarratives = () =>
  api<{ narratives: Narrative[] }>('/api/v1/insights/narratives')

export const createNarrative = (org_id: number, title: string) =>
  api<Narrative>('/api/v1/insights/narratives', {
    method: 'POST', body: JSON.stringify({ org_id, title }),
  })

export type NarrativePatch = {
  title?: string
  scope_label?: string
  intro?: string
  status?: PromotionStatus
  /** Replace-all, ordered — the server rewrites the item list to match. */
  items?: { promotion_id: number; caption_override?: string }[]
}

export const patchNarrative = (id: number, body: NarrativePatch) =>
  api<Narrative>(`/api/v1/insights/narratives/${id}`, {
    method: 'PATCH', body: JSON.stringify(body),
  })

export const deleteNarrative = (id: number) =>
  api(`/api/v1/insights/narratives/${id}`, { method: 'DELETE' })

export const getPromoted = () =>
  api<PromotedFeed>('/api/v1/insights/promoted')


export type ClubReport = {
  run_id: number
  game_code: string
  pack_id: string
  pack_version: string
  title: string
  computed_at: string | null
  published_at: string | null
  /** The match's date (ISO), or null when none is recorded. */
  match_date?: string | null
  /** The score a person entered — the one clubs are shown — or null. */
  score?: { home: number; away: number } | null
  /** The pack's place, most general report first (server PACK_ORDER). */
  order?: number
  /** Each side's crest URL through the club's own team-logo mapping (the
   *  Games list's resolver, server-side), or null: the page draws initials. */
  away_team_logo?: string | null
  home_team_logo?: string | null
}

/** This club's published reports. Club-facing; published-only, own-org-only. */
export const listClubReports = () =>
  api<{ reports: ClubReport[] }>('/api/v1/insights/reports')

/** Report addresses, navigated to rather than fetched: Download saves the
 *  stored page under its readable name. View is the in-app viewer page
 *  (pages/ReportViewer), which frames the embed address and shows a loading
 *  state while the page arrives (2026-10-23: a slow store meant a blank
 *  tab for up to a minute and a half). */
export const clubReportDownloadUrl = (runId: number) =>
  withViewAs(`/api/v1/insights/reports/${runId}/download`)

export const clubReportViewUrl = (runId: number) =>
  // an app page, often opened in a new tab: it adopts the view-as on boot,
  // from the fragment — kept out of every request log
  pageWithViewAs(`/insights/reports/${runId}`)

/** The page itself, inline, for the viewer's sandboxed frame. ?embed=1:
 *  a refusal answers as a page that tells the viewer (insights_api
 *  club_report_view). ?theme= seeds the page's theme before first paint —
 *  without it a pack page falls back to light (Ben, 2026-10-24). */
export const clubReportEmbedUrl = (runId: number, theme: Theme) =>
  withViewAs(`/api/v1/insights/reports/${runId}/view?embed=1&theme=${theme}`)


// ── insight cards ───────────────────────────────────────────────────────────

export type InsightCard = {
  slug: string
  title: string
  /** ISO date (YYYY-MM-DD); the card formats it for reading. */
  date: string
  /** Reserved: the section is one grid today, sub-sections later. */
  category: string | null
}

/** This club's insight cards. Club-facing; own-org-only, like the reports
 *  above — a club with none gets an empty list, not an error. */
export const listInsightCards = () =>
  api<{ cards: InsightCard[] }>('/api/v1/insights/cards')

/** A card's report. Navigated to, not fetched: it is a whole HTML document
 *  served under its own sandbox policy, and it opens in a tab of its own. */
export const insightCardViewUrl = (slug: string, theme?: Theme) =>
  withViewAs(`/api/v1/insights/cards/${pathSeg(slug)}`
    + (theme ? `?theme=${theme}` : ''))


